import { Matrix, Quaternion, Vector3, type TransformNode } from "@babylonjs/core";
import { CAPSTAN, SHIP_LADDER, ladderPosition, type LadderSpec } from "@tiny-seas/shared";

const smooth = (t: number): number => { const p = Math.max(0, Math.min(1, t)); return p * p * (3 - 2 * p); };

/**
 * Render-only step from the authoritative helm point toward the wheel: the helmsman's
 * torso shoulders reach the handles with bent elbows instead of the arms leaving the body.
 */
export const HELM_STEP = 0.45;
/** Forward lean about the hips at the wheel (radians). */
export const HELM_LEAN = 0.3;

/** Eight spoke handles every 45° (`tools/models/ship.py`); wheel-local angles run from the top toward +x. */
const HANDLE_STEP = Math.PI / 4;
/** Grip where each handle leaves the rim (handles span r 0.48–0.61); the fist closes round its axis. */
const HANDLE_RADIUS = 0.53;
const HANDLE_DEPTH = -0.03;
/** Home handles sit at the upper corners: ±45° from the top. */
const HOME = HANDLE_STEP;
/**
 * A hand lets go once its handle turns this far from home, keeping every grip within reach;
 * the one carried outward goes first.
 */
const RELEASE = Math.PI / 180 * 25;
/** Beyond this a hand lets go even while the other is still moving (the wheel spins fast). */
const RELEASE_FORCED = Math.PI / 180 * 32;
const REGRIP_SECONDS = 0.16;
/** The reaching hand clears the wheel face. */
const REGRIP_LIFT = 0.07;

const helmTwist = new Quaternion();

/**
 * Wheel-local hand rotation on the home handle (hanging hand frame: palm faces -z, index
 * along -y). The palm presses the wheel face with the fist's axis along the radial handle,
 * fingers curling over it toward the top of the wheel; the left hand mirrors the right.
 */
function helmHomeRotation(side: number): Quaternion {
  const z = new Vector3(0, 0, -1), y = new Vector3(side * Math.SQRT1_2, -Math.SQRT1_2, 0);
  return Quaternion.FromRotationMatrix(Matrix.FromXYZAxesToRef(Vector3.Cross(y, z), y, z, new Matrix()));
}
const HELM_HOME = [helmHomeRotation(-1), helmHomeRotation(1)] as const;

/**
 * Hand-over-hand helm grips. Each hand holds one spoke handle; once the turning wheel
 * carries it past its release angle it lets go and reaches for the handle then nearest its
 * home corner, so neither hand stretches around the rim at full lock.
 */
export class HelmHands {
  /** Held (or reached-for) handle index per hand; left first. */
  private readonly held = [0, 0];
  private readonly released = [0, 0];
  /** 0…1 travel from the released handle; 1 while gripping. */
  private readonly travel = [1, 1];
  private angle = Number.NaN;
  private rate = 0;

  reset(): void { this.angle = Number.NaN; }

  /** Advance with the animated wheel (`wheel.rotation.z`; handles turn to `angle - rotation.z`). */
  update(wheelAngle: number, dt: number): void {
    if (Number.isNaN(this.angle)) {
      for (let i = 0; i < 2; i++) {
        this.held[i] = this.released[i] = Math.round(((i === 0 ? -HOME : HOME) + wheelAngle) / HANDLE_STEP);
        this.travel[i] = 1;
      }
      this.rate = 0;
    } else if (dt > 0) {
      this.rate += ((wheelAngle - this.angle) / dt - this.rate) * Math.min(1, dt / 0.08);
    }
    this.angle = wheelAngle;
    for (let i = 0; i < 2; i++) this.travel[i] = Math.min(1, this.travel[i]! + Math.max(0, dt) / REGRIP_SECONDS);
    // The hand carried outward goes first; the other waits for it unless it is far behind.
    const first = this.outward(0) >= this.outward(1) ? 0 : 1;
    for (let k = 0; k < 2; k++) {
      const i = k === 0 ? first : 1 - first;
      if (this.travel[i]! < 1) continue;
      const reach = Math.abs(this.outward(i));
      if (reach < RELEASE) continue;
      if (this.travel[1 - i]! < 1 && reach < RELEASE_FORCED) continue;
      // Aim for the handle nearest home when the hand arrives, not where it is now; a frame
      // hitch's spike in the measured rate never sends it more than half a handle further.
      const lead = Math.max(-HANDLE_STEP / 2, Math.min(HANDLE_STEP / 2, this.rate * REGRIP_SECONDS * 0.5));
      const arrival = wheelAngle + lead;
      const target = Math.round(((i === 0 ? -HOME : HOME) + arrival) / HANDLE_STEP);
      if (target === this.held[i]) continue;
      this.released[i] = this.held[i]!;
      this.held[i] = target;
      this.travel[i] = 0;
    }
  }

  /** True while hand `i` is open between handles; it closes as it arrives. */
  reaching(i: number): boolean { return this.travel[i]! < 0.75; }

  /** World contact (the fist's grip centre) and hand rotation for hand `i` (0 left). */
  gripToRef(wheel: TransformNode, i: number, contact: Vector3, rotation: Quaternion): void {
    const t = smooth(this.travel[i]!);
    const beta = (this.released[i]! + (this.held[i]! - this.released[i]!) * t) * HANDLE_STEP;
    const lift = REGRIP_LIFT * Math.sin(Math.PI * t);
    Vector3.TransformCoordinatesFromFloatsToRef(HANDLE_RADIUS * Math.sin(beta), HANDLE_RADIUS * Math.cos(beta), HANDLE_DEPTH - lift,
      wheel.computeWorldMatrix(true), contact);
    // The home rotation turns with the held handle about the axle.
    Quaternion.RotationAxisToRef(Vector3.Forward(), (i === 0 ? -HOME : HOME) - beta, helmTwist);
    wheel.absoluteRotationQuaternion.multiplyToRef(helmTwist, rotation);
    rotation.multiplyToRef(HELM_HOME[i]!, rotation);
  }

  /** Signed angle hand `i`'s handle has turned from its home corner, positive away from the top. */
  private outward(i: number): number {
    const side = i === 0 ? -1 : 1;
    return side * (this.held[i]! * HANDLE_STEP - this.angle - side * HOME);
  }
}

/**
 * Palm contact on capstan bar `bar` of the animated head. Pushers face along the sweep with
 * the head on their right, so the right hand (side +1) grips nearer the head.
 */
export function capstanGripToRef(capstan: TransformNode, bar: number, side: number, out: Vector3): void {
  const a = (bar * Math.PI) / 2, r = CAPSTAN.gripRadius - side * CAPSTAN.gripSpread;
  // The head pivot sits in the ship frame, so bar height is CAPSTAN.barY above the deck origin.
  Vector3.TransformCoordinatesFromFloatsToRef(Math.cos(a) * r, CAPSTAN.barY - capstan.position.y, Math.sin(a) * r, capstan.computeWorldMatrix(true), out);
}

/** The crest is a handhold above the standing climber (hull posts, mast rails), not a deck edge at their feet. */
const crestAbove = (spec: LadderSpec): boolean => spec.crest.y >= spec.head.y + 0.5;

/**
 * Grip height above the climber's feet: each hand holds the rung at or just below it, the right
 * half a rung higher. Hands then range from 0.3 below to 0.55 above the shoulders (1.4 m up),
 * inside the arm's 0.64 m reach with the rungs 0.22 m in front.
 */
const LADDER_GRIP_HEIGHT = 1.55;
/** Fraction of each rung's hold after which the hand lets go and reaches for the next rung. */
const LADDER_REACH_START = 0.62;
const gripPath = new Vector3();

/**
 * Hand `side`'s place in its rung cycle at `progress`, from the body's height on the path alone,
 * so hands stay put while the climber hangs still and retrace when climbing down. The integer
 * part is the rung held; the fraction runs through the hold, reaching after LADDER_REACH_START.
 */
export function ladderGripSlot(progress: number, side: number, spec: LadderSpec = SHIP_LADDER): number {
  ladderPosition(Math.min(progress, 0.75), gripPath, spec);
  return (gripPath.y + LADDER_GRIP_HEIGHT - spec.rungBase) / spec.rungStep + (side > 0 ? 0.5 : 0);
}

/** True while hand `side` is between rungs (open, reaching for the next one). */
export function ladderHandReaching(progress: number, side: number, spec: LadderSpec = SHIP_LADDER): boolean {
  const slot = ladderGripSlot(progress, side, spec), fraction = slot - Math.floor(slot);
  return progress < 0.75 && fraction >= LADDER_REACH_START && fraction <= 0.94;
}

/**
 * Height of hand-hold `k`: authored rungs up to just below the head; above that the hand runs up
 * the handhold rails to the crest grip when one stands above the head (the hull ladder's posts,
 * the mast's rails), or stays on the ladder's top rungs when it does not (tower deck edges).
 */
function holdHeight(k: number, spec: LadderSpec): number {
  const rung = spec.rungBase + k * spec.rungStep;
  if (rung <= spec.head.y - 0.35) return rung;
  return Math.min(rung, crestAbove(spec) ? spec.crest.y : spec.head.y);
}

/**
 * How far (0 … 1) the hands have let go: stepping onto the deck past the crest handholds, or once
 * the highest hold is out of reach below the shoulders (1.4 m up; 0.6 m of reach above the rungs).
 */
export function ladderGripRelease(progress: number, spec: LadderSpec = SHIP_LADDER): number {
  const p = Math.max(0, Math.min(1, progress)), above = crestAbove(spec);
  ladderPosition(p, gripPath, spec);
  const highest = above ? spec.crest.y : spec.head.y;
  return Math.max(above ? smooth((p - 0.78) / 0.08) : 0, smooth((gripPath.y + 0.8 - highest) / 0.2));
}

/** Alternating rung holds, then both hands on the crest handholds, for any ladder in its frame. */
export function ladderGripToRef(root: TransformNode, progress: number, side: number, out: Vector3, spec: LadderSpec = SHIP_LADDER): void {
  const p = Math.max(0, Math.min(1, progress));
  const crestAt = spec.head.y - 0.35, above = crestAbove(spec);
  if (p >= 0.75 && above) {
    out.set(spec.crest.x, spec.crest.y, spec.crest.z + side * spec.crestSpread);
  } else {
    // Past the head with no crest above it, the hands keep the grips they reached the head with.
    const slot = ladderGripSlot(p, side, spec), held = Math.floor(slot);
    const reach = smooth((slot - held - LADDER_REACH_START) / (1 - LADDER_REACH_START));
    const fromHeight = holdHeight(held, spec), toHeight = holdHeight(held + 1, spec);
    const onRails = (k: number) => above && spec.rungBase + k * spec.rungStep > crestAt ? 1 : 0;
    const crestBlend = onRails(held) + (onRails(held + 1) - onRails(held)) * reach;
    // The released hand swings out a little to clear the rung it passes.
    const lift = fromHeight === toHeight ? 0 : Math.sin(reach * Math.PI) * 0.1;
    const rungX = gripPath.x - spec.rungOut;
    out.set(rungX + (spec.crest.x - rungX) * crestBlend + lift, fromHeight + (toHeight - fromHeight) * reach,
      spec.bottom.z + side * (0.22 + crestBlend * (spec.crestSpread - 0.22)));
  }
  Vector3.TransformCoordinatesToRef(out, root.computeWorldMatrix(true), out);
}

/**
 * Authored rear barrel radius by muzzle-pivot station (`tools/models/ship.py`):
 * local +z runs toward the muzzle, the cascabel ball sits behind the flare, and
 * these are the stations the gunner's palms and their recoil travel cover.
 */
const CANNON_PROFILE = [
  [-0.35, 0.075], [-0.27, 0.16], [-0.17, 0.23], [0.02, 0.245], [0.18, 0.235],
] as const;
const CANNON_BREECH_RADIUS = 0.08;

const cannonRadiusScratch = new Vector3();
const cannonAxisX = new Vector3();
const cannonAxisY = new Vector3();
const cannonAxisZ = new Vector3();
const cannonGripMatrix = new Matrix();

/** Outer radius (x) and dr/dz (y) of the authored barrel at a station. */
function cannonRadiusToRef(station: number, out: Vector3): void {
  if (station <= -0.35) {
    // The cascabel is a ball: its radius shrinks behind the barrel's rear face.
    const drop = station + 0.35;
    const radius = Math.sqrt(Math.max(0, CANNON_BREECH_RADIUS * CANNON_BREECH_RADIUS - drop * drop));
    out.set(radius, radius > 1e-6 ? -drop / radius : 0, 0);
    return;
  }
  for (let i = 1; i < CANNON_PROFILE.length; i++) {
    const [fromStation, fromRadius] = CANNON_PROFILE[i - 1]!, [toStation, toRadius] = CANNON_PROFILE[i]!;
    if (station <= toStation) {
      out.set(fromRadius + (toRadius - fromRadius) * (station - fromStation) / (toStation - fromStation),
        (toRadius - fromRadius) / (toStation - fromStation), 0);
      return;
    }
  }
  out.set(CANNON_PROFILE[CANNON_PROFILE.length - 1]![1], 0, 0);
}

/**
 * Palm grip on the animated barrel: the anchor sits on the authored radius at
 * `station`, `azimuth` from the barrel's top toward the hand's own side. The
 * palm stays tangent to the surface (outward normal, knuckle line up the slant)
 * and the fingers wrap down that side, so both hands support the barrel and
 * stay on it while it yaws, elevates or the ship moves.
 */
export function cannonGripToRef(side: number, azimuth: number, station: number, contact: Vector3, rotation: Quaternion): void {
  cannonRadiusToRef(station, cannonRadiusScratch);
  const radius = cannonRadiusScratch.x, slope = cannonRadiusScratch.y;
  const sin = Math.sin(azimuth) * side, cos = Math.cos(azimuth);
  contact.set(sin * radius, cos * radius, station);
  const scale = 1 / Math.hypot(1, slope);
  // x: knuckle line along the slant; y: outward surface normal; z: finger wrap.
  cannonAxisX.set(-side * slope * scale * sin, -side * slope * scale * cos, -side * scale);
  cannonAxisY.set(sin * scale, cos * scale, -slope * scale);
  Vector3.CrossToRef(cannonAxisX, cannonAxisY, cannonAxisZ);
  Matrix.FromXYZAxesToRef(cannonAxisX, cannonAxisY, cannonAxisZ, cannonGripMatrix);
  Quaternion.FromRotationMatrixToRef(cannonGripMatrix, rotation);
}
